import { useEffect, useState } from "react";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import CustomizedTab from "../../../components/Custom/CustomizedTab";
import { Box, IconButton } from "@mui/material";
import { IBreadcrumbsAndMenu, ITab } from "../../../types/global";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ActivityLogDocumentType,
  SalesReturnFindUniqueQuery,
  useSalesReturnFindUniqueQuery,
} from "../../../generated/sales";
import { CustomizedTooltip } from "../../../components/Custom/CustomizedTooltip";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import { ActivityLogSlideInPanel } from "../../../components/UI/SlideInPanel/ActivityLogSlideInPanel";
import DocumentInfoTab from "./DocumentInfoTab";
import GoodsReturnTab from "./GoodsReturnTab";
import { ISalesReturn } from "../../../types/Sales/salesReturn";
import {
  salesReturnSchema,
  salesReturnValidation,
} from "../../../components/Form/Sales/Return/schema";
import { useStateContext } from "../../../contexts/auth-context";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";

const SalesReturnContainer = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");

  const navigate = useNavigate();
  const {
    state: { authUser, permissions },
  } = useStateContext();

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  const { id } = useParams();
  const graphQLClient = createGraphQLClientWithMiddleware("sales");

  const { data, isSuccess } =
    useSalesReturnFindUniqueQuery<SalesReturnFindUniqueQuery>(
      graphQLClient,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: !!id,
        cacheTime: 0,
      }
    );

  useEffect(() => {
    if (isSuccess && permissions?.sales_return?.view_self) {
      const createdBy = data?.SalesReturnFindUnique?.created_by;
      const relatedUserList = data?.SalesReturnFindUnique?.related_user_list;
      if (
        createdBy?.user_unique_id === authUser?.unique_id ||
        relatedUserList?.find(
          (user) => user.user_unique_id === authUser?.unique_id
        )
      ) {
        return;
      } else {
        navigate(`/unauthorized`, {
          replace: true,
        });
      }
    }
  }, [
    authUser?.unique_id,
    data?.SalesReturnFindUnique?.created_by,
    data?.SalesReturnFindUnique?.related_user_list,
    isSuccess,
    navigate,
    permissions?.sales_return?.view_self,
  ]);

  const tabs: ITab[] = [
    {
      label: t("sales.document_info"),
      path: `${pathname}`,
    },
    {
      label: `ใบ${t("inventory.goods_receive.index")} (รับคืน)`,
      path: `${pathname}?tab=goods_return`,
    },
  ];

  const renderTab = (tab: string | null) => {
    switch (tab) {
      case "goods_return":
        return <GoodsReturnTab />;
      default:
        return <DocumentInfoTab />;
    }
  };

  const currentTab = pathname + (tab ? `?tab=${tab}` : "");

  const branch_id = sessionStorage.getItem("branch-id");

  const methods = useForm<ISalesReturn>({
    defaultValues: {
      ...salesReturnSchema,
      branch_id: branch_id ? parseInt(branch_id) : undefined,
    },
    resolver: yupResolver<any>(salesReturnValidation),
  });

  const { control, getValues } = methods;

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("sales.index"),
      to: "/sales",
    },
    {
      name: t("sales.return.index"),
      to: "/sales/return",
    },
    {
      name: id
        ? getValues("unique_id") || "-"
        : t("button.create") + t("sales.return.index"),
    },
  ];

  const status = useWatch({
    control,
    name: "aggrid_status",
  });

  return (
    <FormProvider {...methods}>
      <Box display={"flex"} justifyContent={"space-between"} maxWidth={1040}>
        <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
        {id && (
          <CustomizedTooltip title="ดูความเคลื่อนไหว" enterNextDelay={200}>
            <IconButton
              onClick={() => {
                setOpenDrawer(true);
              }}
              sx={{
                color: (theme) => theme.palette.primary.main,
                padding: 0,
                marginLeft: "4px",
              }}
            >
              <RestoreOutlinedIcon />
            </IconButton>
          </CustomizedTooltip>
        )}
      </Box>
      {(status === "approved" || status === "finished") && (
        <CustomizedTab tabs={tabs} currentTab={currentTab} />
      )}
      {renderTab(tab)}
      {id && (
        <ActivityLogSlideInPanel
          open={openDrawer}
          handleClose={() => setOpenDrawer(false)}
          documentId={id!}
          documentType={ActivityLogDocumentType.SalesReturn}
        />
      )}
    </FormProvider>
  );
};

export default SalesReturnContainer;
